// The draft's autosave (#267 part 2c): live keeps a project as it is edited, with no button for
// it (its Save is "Save to project"), so the draft is saved once edits rest for a moment.
import { useEffect, useLayoutEffect, useRef } from "react";

import type { Timeline } from "./timeline";

/** How long edits rest before the draft is saved: live's 3 s. A test makes it short. */
export const AUTOSAVE = { ms: 3000 };

/**
 * Saves `draft` once it has rested for `AUTOSAVE.ms` while `ready` (changed, valid, nothing in
 * flight, and the member may change it). A draft whose save was tried is not tried again until
 * it changes, so a failed save does not repeat on its own. `draft` is anything a save holds (the
 * timeline, the name), compared by identity. The rest runs from `editedAt`, this tab's own last
 * edit, when it is later than the draft's change: another save taken in changes the draft but
 * never restarts the rest, so a tab that saves every 3 s cannot starve this one (spec
 * live-sync-web.md).
 */
export function useAutosave(
  draft: Timeline | object,
  ready: boolean,
  save: () => Promise<unknown>,
  editedAt = 0,
): void {
  // The save of the latest render, in place before any timer can fire.
  const latest = useRef(save);
  useLayoutEffect(() => {
    latest.current = save;
  });
  const tried = useRef<Timeline | object | null>(null);
  useEffect(() => {
    if (!ready || tried.current === draft) return;
    const rest = editedAt > 0 ? Math.max(0, editedAt + AUTOSAVE.ms - Date.now()) : AUTOSAVE.ms;
    const timer = window.setTimeout(() => {
      tried.current = draft;
      void latest.current();
    }, rest);
    return () => window.clearTimeout(timer);
  }, [draft, ready, editedAt]);
}
